/**
 * 审计日志：追加式，记录"什么时候、对哪辆车、做了什么、结果如何"。
 *
 * ## 为什么它是事故复盘里唯一可信的信息来源
 *
 * 遥测可以被覆盖（后一帧盖住前一帧）、告警可以在恢复后消失、界面状态随页面刷新归零。
 * 只有审计日志是**一条一条加上去的**：参数写入的旧值/新值、危险动作是谁在什么时候发起的、
 * 飞控拒绝了哪一条指令。因此这里的每条记录都必须带齐四样东西：
 * 动作、目标（sysid/compid）、结果（成功/被拒/超时）、时间戳 —— 缺一样，复盘时就只能靠猜。
 *
 * ## 追加式是结构上的保证，不是"代码里小心一点"
 *
 * 每条记录一个键（`entry/{序号}/{写入者}`），因此**没有"重写历史"这个动作**：
 * 追加只发 `put` 新键，删除只发生在轮转里删最旧的。把整份日志存成一个数组再整体覆盖，
 * 就等于每写一条都有一次"整份日志被写坏"的机会 —— 那是拿唯一可信的记录去赌。
 *
 * ## 轮转（必须存在，否则长期使用必然撑爆配额）
 *
 * 默认上限：**2000 条 / 2 MiB（估算）**，用户可以调小。
 * 触发后从最旧的一条开始删，直到回到 **上限的 90%**（摊销：每 200 次追加才轮转一次），
 * 另外还有一层"危险动作豁免"：
 *
 * - `severity === 'danger'` 的条目在删除顺序里最多被豁免 `maxDangerEntries = 500` 条，
 *   于是"最近的关键记录"一定还在 —— 删除是从最旧的开始走的，豁免只是多留一层保险。
 * - 当总条数超过**绝对上限** `maxEntries + maxDangerEntries = 2500` 时，豁免失效（从最旧的开始删）。
 *   没有这一条，豁免会把上限架空，配额照样被撑爆。
 * - 字节上限同样参与判断：删到条数达标但字节仍超时继续删。
 * - 轮转本身会追加一条 `audit.rotate` 摘要（删了多少、覆盖哪段时间），
 *   于是"日志被裁剪过"这件事本身也可审计，而不是让用户以为历史只有这么长。
 *
 * ## 配额满时先轮转再重试
 *
 * 写审计条目遇到 `QuotaExceededError` 时，先强制轮转到上限的 50%，再重试一次。
 * 这是审计日志必须有轮转的**最直接理由**：它是唯一能自己腾出空间的功能，
 * 而它要记的恰恰是"配额已满、这次参数没写进去"这类最需要留痕的事件。
 * 重试仍失败则返回可读失败（含"清理历史记录"的建议），绝不让保存流程崩掉。
 *
 * ## 并发写
 *
 * 键里带写入者 id，两个标签页同时追加**两条都会留下**（不会互相覆盖）。
 * `seq` 取自时钟（`max(上一个 + 1, 当前时间)`），因此跨标签页的顺序与时钟一致，
 * 但不保证严格全序 —— 同一毫秒内的先后按写入者 id 排，这是确定的（可复现）。
 * 同一条记录被两处同时改的通用策略是"最后写入胜出 + 审计留下两者"，
 * 审计日志这一侧就是那个"两者都留下"的落点。
 */

import { attemptSave, type SaveOutcome, type StorageFailure } from './errors';
import {
  AUDIT_ENTRY_PREFIX,
  AUDIT_STATS_KEY,
  RECORD_FORMAT,
  auditEntryKey,
  isReadableFormat,
  isRecordLike,
  parseAuditKey,
  readFiniteNumber,
  readString,
} from './keys';
import { estimateStorableBytes } from './serialization';
import type { KeyValueStore, StoreOperation } from './store';

export type AuditSeverity = 'info' | 'warning' | 'danger';
export type AuditOutcome = 'ok' | 'rejected' | 'timeout' | 'error' | 'aborted';

/** 车辆标识。审计与飞前检查共用同一形状，两者才能按车对上 */
export interface AuditTarget {
  readonly systemId: number;
  readonly componentId: number;
}

/**
 * "没有具体车辆"的目标：地面站自身的动作（例如日志轮转、设置重置）。
 * 用 sysid 0 而不是随便挑一个车号，避免复盘时把地面站动作误判成某辆车的动作。
 */
export const GROUND_STATION_TARGET: AuditTarget = Object.freeze({ systemId: 0, componentId: 0 });

export interface AuditParamChange {
  /** 参数名（ArduPilot 的 `PARAM_NAME`） */
  readonly name: string;
  /** 多实例参数的实例号（例如 `SERVO1_FUNCTION` 的 1）；非多实例参数为 undefined */
  readonly index?: number;
  readonly oldValue: number | string | null;
  readonly newValue: number | string | null;
}

export interface AuditEntryInit {
  /** 动作标识，建议用 `域.动作` 形式，例如 `param.set`、`command.arm`、`mission.upload` */
  readonly action: string;
  readonly target: AuditTarget;
  readonly outcome: AuditOutcome;
  /** 人读摘要，直接显示在日志列表里 */
  readonly summary: string;
  readonly severity?: AuditSeverity;
  readonly changes?: readonly AuditParamChange[];
  /** 其它上下文（模式名、航点条数、失败原因对象里的字段……）。必须是可结构化克隆的普通数据 */
  readonly detail?: Readonly<Record<string, unknown>>;
  readonly vehicleLabel?: string;
  readonly durationMs?: number;
  readonly error?: string;
  readonly at?: number;
}

export interface AuditEntry {
  readonly format: number;
  /** 单调序号（不保证跨标签页严格全序，见文件头） */
  readonly seq: number;
  readonly at: number;
  /** 写入者 id（每个审计日志实例一个），用于跨标签页区分来源 */
  readonly writer: string;
  readonly action: string;
  readonly severity: AuditSeverity;
  readonly outcome: AuditOutcome;
  readonly target: AuditTarget;
  readonly summary: string;
  readonly changes?: readonly AuditParamChange[];
  readonly detail?: Readonly<Record<string, unknown>>;
  readonly vehicleLabel?: string;
  readonly durationMs?: number;
  readonly error?: string;
}

/**
 * 危险动作清单。
 *
 * 判据是"做错了会导致人身或财产损失，且没有明显提示就看不出来"：
 * 解锁、电机测试、写参数、上传/启动航线、校准、刷固件、重启飞控、改机架类型。
 * 这份清单只影响 `severity`（也就是轮转时是否豁免），不影响是否记录 —— 所有动作都记录。
 */
export const AUDIT_DANGEROUS_ACTIONS: readonly string[] = [
  'command.arm',
  'motor.test',
  'param.set',
  'param.reset-to-default',
  'param.save-to-flash',
  'mission.upload',
  'mission.start',
  'mission.clear',
  'fence.upload',
  'rally.upload',
  'calibration.start',
  'frame.set',
  'firmware.flash',
  'vehicle.reboot',
];

export function isDangerousAction(action: string): boolean {
  return AUDIT_DANGEROUS_ACTIONS.includes(action);
}

/**
 * 推断严重级别。显式给出的 `severity` 优先，其余按"动作危险程度 + 结果"推断：
 * 危险动作即使成功也记 `danger`（"成功的解锁"同样要能在复盘里找到），
 * 被拒/超时/出错则至少 `warning`（失败本身就是要看的东西）。
 */
export function auditSeverityOf(
  action: string,
  outcome: AuditOutcome,
  explicit?: AuditSeverity,
): AuditSeverity {
  if (explicit !== undefined) return explicit;
  if (isDangerousAction(action)) return 'danger';
  return outcome === 'ok' ? 'info' : 'warning';
}

export interface AuditRetention {
  /** 常规条数上限，默认 2000 */
  readonly maxEntries: number;
  /** 估算字节上限，默认 2 MiB */
  readonly maxBytes: number;
  /** 危险条目的豁免上限，默认 500 */
  readonly maxDangerEntries: number;
  /** 轮转目标比例（相对上限），默认 0.9；配额触发时用 0.5 */
  readonly targetRatio: number;
}

export const AUDIT_RETENTION_DEFAULTS: AuditRetention = Object.freeze({
  maxEntries: 2000,
  maxBytes: 2 * 1024 * 1024,
  maxDangerEntries: 500,
  targetRatio: 0.9,
});

/** 配额触发时删除到什么比例：比常规轮转更狠，因为要一次腾出空间 */
const QUOTA_ROTATION_RATIO = 0.5;
/** 配额兜底的第二级：只留最近这一小部分（仍然从最旧的开始删） */
const QUOTA_DEEP_ROTATION_RATIO = 0.05;

/** 每多少次追加核对一次真实条数（跨标签页写入会让内存计数偏小） */
const VERIFY_EVERY_APPENDS = 200;

/** 一次事务里最多删多少条：让单个事务不至于大到长时间占住数据库 */
const DELETE_BATCH_SIZE = 200;

export interface AuditStats {
  readonly count: number;
  readonly bytes: number;
  readonly lastSeq: number;
  readonly rotatedAt: number;
  /** 累计被轮转删掉的条数，用于在界面上说明"历史曾经更长" */
  readonly dropped: number;
}

export interface AuditRotationResult {
  readonly reason: 'auto' | 'manual' | 'quota';
  readonly removed: number;
  readonly kept: number;
  readonly protectedDanger: number;
  readonly bytes: number;
  /** 被删掉的时间范围，界面据此告诉用户"历史从什么时候开始变短" */
  readonly oldestRemovedAt: number | null;
  readonly newestRemovedAt: number | null;
}

export type AuditAppendResult =
  | {
      readonly ok: true;
      readonly entry: AuditEntry;
      /** 这次追加顺带触发的轮转；没有轮转时为 null */
      readonly rotated: AuditRotationResult | null;
    }
  | { readonly ok: false; readonly failure: StorageFailure };

export interface AuditLogOptions {
  readonly now?: () => number;
  readonly retention?: Partial<AuditRetention>;
  /** 写入者 id；默认每个实例随机生成，测试可注入固定值 */
  readonly writerId?: string;
  readonly onFailure?: (failure: StorageFailure) => void;
  readonly onRotate?: (result: AuditRotationResult) => void;
}

export interface AuditListOptions {
  readonly limit?: number;
  /** 只取 `at >= since` 的记录（毫秒时间戳） */
  readonly since?: number;
  /** 只取 `at <= until` 的记录 */
  readonly until?: number;
  readonly severity?: AuditSeverity;
  readonly action?: string;
  readonly target?: AuditTarget;
}

function readSeverity(value: unknown): AuditSeverity | null {
  return value === 'info' || value === 'warning' || value === 'danger' ? value : null;
}

function readOutcome(value: unknown): AuditOutcome | null {
  return value === 'ok' || value === 'rejected' || value === 'timeout' || value === 'error' || value === 'aborted'
    ? value
    : null;
}

function readTarget(value: unknown): AuditTarget | null {
  if (!isRecordLike(value)) return null;
  const systemId = readFiniteNumber(value['systemId']);
  const componentId = readFiniteNumber(value['componentId']);
  if (systemId === null || componentId === null) return null;
  return { systemId, componentId };
}

function readParamChange(value: unknown): AuditParamChange | null {
  if (!isRecordLike(value)) return null;
  const name = readString(value['name']);
  if (name === null) return null;
  const index = readFiniteNumber(value['index']);
  const oldValue = value['oldValue'];
  const newValue = value['newValue'];
  return {
    name,
    ...(index === null ? {} : { index }),
    oldValue: typeof oldValue === 'number' || typeof oldValue === 'string' ? oldValue : null,
    newValue: typeof newValue === 'number' || typeof newValue === 'string' ? newValue : null,
  };
}

/** 读取校验：坏掉的条目返回 null（调用方跳过它，而不是把半条记录显示成一条完整的审计） */
export function readAuditEntry(value: unknown): AuditEntry | null {
  if (!isRecordLike(value)) return null;
  if (!isReadableFormat(value['format'])) return null;
  const seq = readFiniteNumber(value['seq']);
  const at = readFiniteNumber(value['at']);
  const writer = readString(value['writer']);
  const action = readString(value['action']);
  const severity = readSeverity(value['severity']);
  const outcome = readOutcome(value['outcome']);
  const target = readTarget(value['target']);
  const summary = readString(value['summary']);
  if (
    seq === null ||
    at === null ||
    writer === null ||
    action === null ||
    severity === null ||
    outcome === null ||
    target === null ||
    summary === null
  ) {
    return null;
  }

  const rawChanges = value['changes'];
  let changes: AuditParamChange[] | undefined;
  if (Array.isArray(rawChanges)) {
    changes = [];
    for (const entry of rawChanges) {
      const change = readParamChange(entry);
      if (change !== null) changes.push(change);
    }
  }
  const detail = isRecordLike(value['detail']) ? value['detail'] : undefined;
  const vehicleLabel = readString(value['vehicleLabel']);
  const durationMs = readFiniteNumber(value['durationMs']);
  const error = readString(value['error']);

  return {
    format: RECORD_FORMAT,
    seq,
    at,
    writer,
    action,
    severity,
    outcome,
    target,
    summary,
    ...(changes === undefined || changes.length === 0 ? {} : { changes }),
    ...(detail === undefined ? {} : { detail }),
    ...(vehicleLabel === null ? {} : { vehicleLabel }),
    ...(durationMs === null ? {} : { durationMs }),
    ...(error === null ? {} : { error }),
  };
}

export function readAuditStats(value: unknown): AuditStats | null {
  if (!isRecordLike(value)) return null;
  const count = readFiniteNumber(value['count']);
  const bytes = readFiniteNumber(value['bytes']);
  const lastSeq = readFiniteNumber(value['lastSeq']);
  if (count === null || bytes === null || lastSeq === null) return null;
  return {
    count,
    bytes,
    lastSeq,
    rotatedAt: readFiniteNumber(value['rotatedAt']) ?? 0,
    dropped: readFiniteNumber(value['dropped']) ?? 0,
  };
}

export class AuditLog {
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private retention: AuditRetention;
  private readonly writerId: string;
  private readonly onFailure: ((failure: StorageFailure) => void) | undefined;
  private readonly onRotate: ((result: AuditRotationResult) => void) | undefined;

  private stats: AuditStats | null = null;
  private appendsSinceVerify = 0;
  private rotating = false;
  /**
   * 上一次轮转什么都没删掉（危险条目豁免 + 用户把上限调得极小之类的退化配置）。
   * 没有这个标记，每次追加都会把上千条记录读一遍却删不掉任何东西。
   */
  private rotationStalled = false;

  constructor(store: KeyValueStore, options: AuditLogOptions = {}) {
    this.store = store;
    this.now = options.now ?? Date.now;
    this.writerId = options.writerId ?? defaultWriterId();
    this.onFailure = options.onFailure;
    this.onRotate = options.onRotate;
    const retention = options.retention ?? {};
    this.retention = {
      maxEntries: retention.maxEntries ?? AUDIT_RETENTION_DEFAULTS.maxEntries,
      maxBytes: retention.maxBytes ?? AUDIT_RETENTION_DEFAULTS.maxBytes,
      maxDangerEntries: retention.maxDangerEntries ?? AUDIT_RETENTION_DEFAULTS.maxDangerEntries,
      targetRatio: retention.targetRatio ?? AUDIT_RETENTION_DEFAULTS.targetRatio,
    };
  }

  /**
   * 调整容量上限（设置页改完立刻生效，下次打开由设置里持久化的值决定）。
   * 只改上限，不主动轮转：改小上限后由下一次追加触发轮转，避免保存设置时卡在删除上。
   */
  setRetention(patch: Partial<AuditRetention>): void {
    this.retention = { ...this.retention, ...patch };
    this.rotationStalled = false;
  }

  /**
   * 追加一条记录。
   *
   * 配额满时先轮转再重试一次（见文件头）；两次都失败才返回失败，
   * 且失败描述里带着"清理历史记录"的建议，调用方可以直接显示。
   */
  async append(init: AuditEntryInit): Promise<AuditAppendResult> {
    try {
      return await this.appendInternal(init);
    } catch (error) {
      // 追加审计记录**永不抛错**：调用它的大多是"动作刚刚失败"的路径，
      // 一个未捕获的拒绝会盖住真正要报给用户的错误
      const failure = await classify('写入审计日志', error);
      this.onFailure?.(failure);
      return { ok: false, failure };
    }
  }

  private async appendInternal(init: AuditEntryInit): Promise<AuditAppendResult> {
    const at = init.at ?? this.now();
    const stats = await this.ensureStats();
    const seq = Math.max(stats.lastSeq + 1, at);
    const entry: AuditEntry = {
      format: RECORD_FORMAT,
      seq,
      at,
      writer: this.writerId,
      action: init.action,
      severity: auditSeverityOf(init.action, init.outcome, init.severity),
      outcome: init.outcome,
      target: init.target,
      summary: init.summary,
      ...(init.changes === undefined || init.changes.length === 0 ? {} : { changes: init.changes }),
      ...(init.detail === undefined ? {} : { detail: init.detail }),
      ...(init.vehicleLabel === undefined ? {} : { vehicleLabel: init.vehicleLabel }),
      ...(init.durationMs === undefined ? {} : { durationMs: init.durationMs }),
      ...(init.error === undefined ? {} : { error: init.error }),
    };
    const key = auditEntryKey(entry.seq, entry.writer);
    const bytes = estimateStorableBytes(entry);

    let written = await attemptSave(
      '写入审计日志',
      async () => {
        await this.store.put('audit-log', key, entry);
      },
      { collection: 'audit-log' },
    );

    let rotated: AuditRotationResult | null = null;
    if (!written.ok && written.failure.code === 'quota-exceeded') {
      // 审计日志是唯一能自己腾出空间的功能，而"配额已满"恰恰是最需要留痕的事件。
      // 两级清理：先按常规比例腾一半，仍不够就只留最近的一小部分（依旧从最旧的开始删，
      // 所以最近的关键记录始终安全）。两次都失败才如实返回失败
      for (const ratio of [QUOTA_ROTATION_RATIO, QUOTA_DEEP_ROTATION_RATIO]) {
        if (written.ok || written.failure.code !== 'quota-exceeded') break;
        rotated = await this.rotate('quota', { ratio });
        written = await attemptSave(
          '写入审计日志（配额已满后重试）',
          async () => {
            await this.store.put('audit-log', key, entry);
          },
          { collection: 'audit-log' },
        );
      }
    }

    if (!written.ok) {
      this.onFailure?.(written.failure);
      return { ok: false, failure: written.failure };
    }

    this.stats = {
      count: stats.count + 1,
      bytes: stats.bytes + bytes,
      lastSeq: seq,
      rotatedAt: stats.rotatedAt,
      dropped: stats.dropped,
    };

    // 跨标签页写入会让内存计数偏小，定期用真实键数核对一次
    this.appendsSinceVerify += 1;
    if (this.appendsSinceVerify >= VERIFY_EVERY_APPENDS && !this.rotating) {
      this.appendsSinceVerify = 0;
      await this.refreshCount();
    }

    // 刚刚已经轮转过（配额路径）就不重复轮转；上一次轮转什么都没删掉时也不再空转，
    // 否则每次追加都会把两千条记录读一遍而删不掉任何东西
    if (rotated === null && !this.rotating && !this.rotationStalled && this.overLimit()) {
      rotated = await this.rotate('auto');
    }

    return { ok: true, entry, rotated };
  }

  /**
   * 列出记录，默认**最新在前**（日志界面就是按这个顺序看的）。
   *
   * `since` / `until` 靠键里的定长时间戳做二分裁剪，因此"只看最近十分钟"不需要先把
   * 两千条都读出来；`severity` / `action` / `target` 是解码后再过滤 —— 这正是"没有二级索引"
   * 的代价，换来的是两个实现都不需要维护索引一致性。
   */
  async list(options: AuditListOptions = {}): Promise<readonly AuditEntry[]> {
    const limit = options.limit ?? 200;
    const keys = await this.store.keys('audit-log', AUDIT_ENTRY_PREFIX);
    const start = options.since === undefined ? 0 : lowerBoundBySeq(keys, options.since);
    const entries: AuditEntry[] = [];
    for (let index = keys.length - 1; index >= start && entries.length < limit; index -= 1) {
      const key = keys[index];
      if (key === undefined) continue;
      if (options.until !== undefined) {
        const info = parseAuditKey(key);
        if (info !== null && info.seq > options.until) continue;
      }
      const entry = readAuditEntry(await this.store.get('audit-log', key));
      if (entry === null) continue;
      if (options.severity !== undefined && entry.severity !== options.severity) continue;
      if (options.action !== undefined && entry.action !== options.action) continue;
      if (options.target !== undefined && !sameTarget(entry.target, options.target)) continue;
      entries.push(entry);
    }
    return entries;
  }

  async count(): Promise<number> {
    return (await this.store.keys('audit-log', AUDIT_ENTRY_PREFIX)).length;
  }

  /**
   * 当前统计（条数 / 估算字节 / 已轮转丢弃的条数）。
   * 界面用它显示"已存 1832 / 2000 条"，也是"该清理了"的判据。
   */
  async readStats(): Promise<AuditStats> {
    try {
      return await this.ensureStats();
    } catch (error) {
      const failure = await classify('读取审计日志统计', error);
      this.onFailure?.(failure);
      return { count: 0, bytes: 0, lastSeq: 0, rotatedAt: 0, dropped: 0 };
    }
  }

  /**
   * 手动轮转（"清理历史记录"按钮走这里）。`reason` 只影响摘要里的文案。
   */
  async rotate(reason: 'auto' | 'manual' | 'quota' = 'manual', options: { readonly ratio?: number } = {}): Promise<AuditRotationResult> {
    if (this.rotating) {
      // 重入保护：轮转过程中追加的摘要条目不该再触发一次轮转
      return { reason, removed: 0, kept: this.stats?.count ?? 0, protectedDanger: 0, bytes: this.stats?.bytes ?? 0, oldestRemovedAt: null, newestRemovedAt: null };
    }
    this.rotating = true;
    try {
      const result = await this.performRotation(reason, options.ratio);
      this.onRotate?.(result);
      return result;
    } finally {
      this.rotating = false;
    }
  }

  /** 清空整份日志（用户明确要求"清理历史记录"时）。返回删掉的条数 */
  async clear(): Promise<SaveOutcome<number>> {
    const outcome = await attemptSave(
      '清空审计日志',
      async () => {
        const keys = await this.store.keys('audit-log', AUDIT_ENTRY_PREFIX);
        for (let offset = 0; offset < keys.length; offset += DELETE_BATCH_SIZE) {
          const slice = keys.slice(offset, offset + DELETE_BATCH_SIZE);
          await this.store.batch(
            slice.map((key) => ({ kind: 'delete', collection: 'audit-log', key }) as const),
          );
        }
        this.stats = { count: 0, bytes: 0, lastSeq: this.stats?.lastSeq ?? 0, rotatedAt: this.now(), dropped: keys.length };
        this.rotationStalled = false;
        await this.persistStats();
        return keys.length;
      },
      { collection: 'audit-log' },
    );
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }

  /**
   * 真正干活的部分。
   *
   * 这里把**所有**条目的值读一遍来重算字节数：字节统计必须来自实际内容（估算值随记录大小变化），
   * 而轮转本来就要按顺序看条目以决定谁能删。代价是每次轮转 O(条数) 次读取，
   * 摊到每次追加上只有约 (上限 × (1 - 目标比例))⁻¹ 的量级 —— 2000 条上限、0.9 目标时，
   * 每 200 次追加轮转一次，也就是每次追加约 10 次读取。这个代价换取的是"上限真的被执行"。
   */
  private async performRotation(reason: 'auto' | 'manual' | 'quota', ratioOverride?: number): Promise<AuditRotationResult> {
    const keys = await this.store.keys('audit-log', AUDIT_ENTRY_PREFIX);
    const entries: { readonly key: string; readonly entry: AuditEntry | null; readonly bytes: number }[] = [];
    let totalBytes = 0;
    for (const key of keys) {
      const raw = await this.store.get('audit-log', key);
      if (raw === undefined) continue;
      const bytes = estimateStorableBytes(raw);
      totalBytes += bytes;
      entries.push({ key, entry: readAuditEntry(raw), bytes });
    }

    const ratio = ratioOverride ?? (reason === 'quota' ? QUOTA_ROTATION_RATIO : this.retention.targetRatio);
    const targetEntries = Math.max(0, Math.floor(this.retention.maxEntries * ratio));
    const targetBytes = Math.max(0, Math.floor(this.retention.maxBytes * ratio));
    const hardMax = this.retention.maxEntries + this.retention.maxDangerEntries;

    let remaining = entries.length;
    let remainingBytes = totalBytes;
    let protectedDanger = 0;
    const deletions: StoreOperation[] = [];
    let oldestRemovedAt: number | null = null;
    let newestRemovedAt: number | null = null;

    // 从最旧的一条开始删：**最近**的记录（包括最近的危险动作）在结构上就不可能被删到
    for (const item of entries) {
      if (remaining <= targetEntries && remainingBytes <= targetBytes) break;
      const isDanger = item.entry?.severity === 'danger';
      // 危险条目豁免，但绝对上限一到就失效（否则豁免会把上限架空，配额照样被撑爆）
      if (isDanger && protectedDanger < this.retention.maxDangerEntries && entries.length <= hardMax) {
        protectedDanger += 1;
        continue;
      }
      deletions.push({ kind: 'delete', collection: 'audit-log', key: item.key });
      remaining -= 1;
      remainingBytes -= item.bytes;
      const at = item.entry?.at ?? null;
      if (at !== null) {
        oldestRemovedAt ??= at;
        newestRemovedAt = at;
      }
    }

    for (let offset = 0; offset < deletions.length; offset += DELETE_BATCH_SIZE) {
      await this.store.batch(deletions.slice(offset, offset + DELETE_BATCH_SIZE));
    }

    const removed = deletions.length;
    const targetMet = remaining <= targetEntries && remainingBytes <= targetBytes;
    this.rotationStalled = removed === 0 && !targetMet;
    const result: AuditRotationResult = {
      reason,
      removed,
      kept: remaining,
      protectedDanger,
      bytes: remainingBytes,
      oldestRemovedAt,
      newestRemovedAt,
    };

    // 摘要条目让"日志被裁剪过"本身也可审计。它**不走** append()，
    // 否则会再次触发上限判断，形成"轮转 → 写摘要 → 又超限 → 再轮转"的循环
    /*
     * 配额兜底的代价要说清楚：轮转自己也要写摘要与统计，所以在"配额刚好只够几条记录"的
     * 极端拥挤下，腾出来的空间可能被这些簿记吃掉。两级清理（先 50%，再 5%）覆盖了绝大多数
     * 真实场景；极端情况仍失败时如实返回可读失败，由用户去清理历史记录 ——
     * 这比"删掉用户的快照来腾地方"要好得多。
     */
    let summaryBytes = 0;
    try {
      const summaryAt = this.now();
      const summaryEntry: AuditEntry = {
        format: RECORD_FORMAT,
        seq: Math.max((this.stats?.lastSeq ?? 0) + 1, summaryAt),
        at: summaryAt,
        writer: this.writerId,
        action: 'audit.rotate',
        severity: 'warning',
        outcome: 'ok',
        target: GROUND_STATION_TARGET,
        summary: `审计日志已轮转（${rotationReasonLabel(reason)}）：删除 ${removed} 条，保留 ${remaining} 条`,
        detail: {
          reason,
          removed,
          kept: remaining,
          protectedDanger,
          oldestRemovedAt,
          newestRemovedAt,
        },
      };
      summaryBytes = estimateStorableBytes(summaryEntry);
      await this.store.put('audit-log', auditEntryKey(summaryEntry.seq, summaryEntry.writer), summaryEntry);
      this.stats = {
        count: remaining + 1,
        bytes: remainingBytes + summaryBytes,
        lastSeq: summaryEntry.seq,
        rotatedAt: summaryEntry.at,
        dropped: (this.stats?.dropped ?? 0) + removed,
      };
    } catch (error) {
      // 摘要写不进去（多半还是配额）不能让轮转本身失败：删除已经生效，日志确实被裁剪了
      const failure = await classify('写入轮转摘要', error);
      this.onFailure?.(failure);
      this.stats = {
        count: remaining,
        bytes: remainingBytes,
        lastSeq: this.stats?.lastSeq ?? 0,
        rotatedAt: this.now(),
        dropped: (this.stats?.dropped ?? 0) + removed,
      };
    }

    await this.persistStats();
    return result;
  }

  private overLimit(): boolean {
    const stats = this.stats;
    if (stats === null) return false;
    return stats.count > this.retention.maxEntries || stats.bytes > this.retention.maxBytes;
  }

  /**
   * 取（或建立）计数统计。
   *
   * 统计落盘在 `audit-log/stats` 上，于是打开应用不需要把两千条记录读一遍；
   * 落盘的统计**只在条数与真实键数一致时才采信**，否则重算一遍 ——
   * 上一次写到一半崩溃、另一个标签页刚追加过，都会让落盘的统计偏小，
   * 而偏小的计数会让上限悄悄失效。
   */
  private async ensureStats(): Promise<AuditStats> {
    if (this.stats !== null) return this.stats;
    const keys = await this.store.keys('audit-log', AUDIT_ENTRY_PREFIX);
    const stored = readAuditStats(await this.store.get('audit-log', AUDIT_STATS_KEY));
    if (stored !== null && stored.count === keys.length) {
      this.stats = stored;
      return stored;
    }

    let bytes = 0;
    let lastSeq = 0;
    let dropped = stored?.dropped ?? 0;
    for (const key of keys) {
      const raw = await this.store.get('audit-log', key);
      if (raw !== undefined) bytes += estimateStorableBytes(raw);
      const info = parseAuditKey(key);
      if (info !== null) lastSeq = Math.max(lastSeq, info.seq);
    }
    const stats: AuditStats = { count: keys.length, bytes, lastSeq, rotatedAt: stored?.rotatedAt ?? 0, dropped };
    this.stats = stats;
    await this.persistStats();
    return stats;
  }

  private async refreshCount(): Promise<void> {
    const count = await this.count();
    const stats = this.stats;
    if (stats === null || stats.count === count) return;
    // 条数变少说明空间确实被腾出来了（多半是另一个标签页轮转过），空转判定可以重来
    if (count < stats.count) this.rotationStalled = false;
    this.stats = { ...stats, count };
  }

  private async persistStats(): Promise<void> {
    const stats = this.stats;
    if (stats === null) return;
    const outcome = await attemptSave(
      '保存审计日志统计',
      async () => {
        await this.store.put('audit-log', AUDIT_STATS_KEY, { format: RECORD_FORMAT, ...stats });
      },
      { collection: 'audit-log' },
    );
    // 统计只是诊断信息：写不进去不该让"追加审计记录"这件事失败
    if (!outcome.ok) this.onFailure?.(outcome.failure);
  }
}

function rotationReasonLabel(reason: 'auto' | 'manual' | 'quota'): string {
  if (reason === 'quota') return '配额已满，强制腾出空间';
  if (reason === 'manual') return '用户手动清理';
  return '达到容量上限';
}

function sameTarget(left: AuditTarget, right: AuditTarget): boolean {
  return left.systemId === right.systemId && left.componentId === right.componentId;
}

/** 找第一个序号 ≥ `since` 的下标。键是定长序号前缀，因此字典序与序号同序 */
function lowerBoundBySeq(keys: readonly string[], since: number): number {
  let low = 0;
  let high = keys.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    const key = keys[middle];
    const seq = key === undefined ? Number.NaN : (parseAuditKey(key)?.seq ?? Number.NaN);
    if (Number.isNaN(seq) || seq < since) low = middle + 1;
    else high = middle;
  }
  return low;
}

function defaultWriterId(): string {
  // 每个审计日志实例一个写入者 id：两个标签页同时追加时键不会撞，两条记录都会留下
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

async function classify(operation: string, error: unknown): Promise<StorageFailure> {
  const outcome = await attemptSave(operation, () => {
    throw error;
  });
  if (outcome.ok) throw new Error('不可能到达：抛错时 attemptSave 一定返回失败');
  return outcome.failure;
}
